import React, { useState, useEffect } from 'react';
import { Box, Container, Paper, Typography, Button, Divider } from '@mui/material';

interface LogEntry {
  timestamp: string;
  message: string;
  type: 'log' | 'error' | 'warn';
}

const DebugLogs: React.FC = () => {
  const [logs, setLogs] = useState<LogEntry[]>([]);

  const loadLogs = () => {
    const storedLogs = localStorage.getItem('debugLogs');
    if (storedLogs) {
      setLogs(JSON.parse(storedLogs));
    }
  };

  useEffect(() => {
    loadLogs();
    // Refresh every 500ms to catch new logs during redirects
    const interval = setInterval(loadLogs, 500);
    return () => clearInterval(interval);
  }, []);

  const clearLogs = () => {
    localStorage.removeItem('debugLogs');
    localStorage.removeItem('protectedRouteLogs');
    localStorage.removeItem('loginLogs');
    setLogs([]);
  };

  const copyLogs = () => {
    const text = logs.map(log => `[${log.timestamp}] ${log.type.toUpperCase()}: ${log.message}`).join('\n');
    navigator.clipboard.writeText(text);
    alert('Logs copied to clipboard!');
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          🐛 Debug Logs
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          All console logs are captured here. Auto-refreshes every 500ms.
        </Typography>

        <Box sx={{ mt: 2, mb: 2, display: 'flex', gap: 2 }}>
          <Button variant="contained" onClick={loadLogs}>
            Refresh
          </Button>
          <Button variant="outlined" onClick={copyLogs}>
            Copy All Logs
          </Button>
          <Button variant="outlined" color="error" onClick={clearLogs}>
            Clear Logs
          </Button>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{
          maxHeight: '70vh',
          overflow: 'auto',
          backgroundColor: '#1e1e1e',
          p: 2,
          borderRadius: 1,
          fontFamily: 'monospace',
          fontSize: '0.875rem'
        }}>
          {logs.length === 0 ? (
            <Typography color="text.secondary">No logs captured yet.</Typography>
          ) : (
            logs.map((log, index) => (
              <Box
                key={index}
                sx={{
                  mb: 1,
                  color: log.type === 'error' ? '#ff5555' : log.type === 'warn' ? '#ffaa00' : '#ffffff'
                }}
              >
                <Typography
                  component="span"
                  sx={{
                    color: '#888',
                    fontSize: '0.75rem',
                    mr: 1
                  }}
                >
                  [{log.timestamp}]
                </Typography>
                <Typography
                  component="span"
                  sx={{
                    color: log.type === 'error' ? '#ff5555' : log.type === 'warn' ? '#ffaa00' : '#50fa7b',
                    fontWeight: 'bold',
                    mr: 1
                  }}
                >
                  {log.type.toUpperCase()}:
                </Typography>
                <Typography component="span" sx={{ whiteSpace: 'pre-wrap' }}>
                  {log.message}
                </Typography>
              </Box>
            ))
          )}
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
          Total logs: {logs.length} | Last 200 shown
        </Typography>
      </Paper>
    </Container>
  );
};

export default DebugLogs;
